import type { Routine } from "instantdb/types";
import { instantClientDB } from "instantdb/instantClient";
import { id } from "@instantdb/admin";

// Get all routines for a user
export function getRoutines(userId?: string) {
  const { data, isLoading } = instantClientDB.useQuery({
    routines: {
      $: {
        where: {
          userId: userId ?? "",
        },
      },
      healthArea: {},
      daysCompleted: {},
    },
  });

  if (!userId) {
    return {
      routines: [],
      isLoading: false,
    };
  }

  return {
    routines: data?.routines ?? [],
    isLoading: !!userId && isLoading,
  };
}

export async function getRoutinesOnce(userId?: string) {
  const { data } = await instantClientDB.queryOnce({
    routines: {
      $: {
        where: {
          userId: userId ?? "",
        },
      },
      healthArea: {},
      daysCompleted: {},
    },
  });

  if (!userId) {
    return {
      routines: [],
    };
  }

  return {
    routines: data?.routines ?? [],
  };
}

// Create or update a routine
export async function upsertRoutine(routine: Routine) {
  // @ts-expect-error healthArea might exist on routine type
  const { id: inputId, healthArea, daysCompleted, ...routineData } = routine;
  const routineId = inputId ?? id();

  // Build transaction steps for routine
  const routineTxSteps = [
    instantClientDB.tx.routines[routineId].update({
      ...routineData,
    }),
  ];

  // If healthArea is provided with an id, link the routine to that healthArea
  // @ts-expect-error accessing healthArea id
  if (routine.healthArea?.id) {
    routineTxSteps.push(
      // @ts-expect-error accessing healthArea id
      instantClientDB.tx.healthAreas[routine.healthArea.id].link({ routines: [routineId] }),
    );
  }

  // Execute routine transaction first
  await instantClientDB.transact(routineTxSteps).catch((error) => {
    console.error("Error upserting routine:", routine, error);
  });

  return routineId;
}

// Delete a routine
export async function deleteRoutine(id: string) {
  await instantClientDB.transact([instantClientDB.tx.routines[id].delete()]);
}

// Update or create a day completion record
export async function updateDayCompleted(
  routineId: string,
  userId: string,
  date: string,
  stepsCompleted: string[],
  completed?: boolean,
) {
  // First, check if a daysCompleted record already exists for this routine and date
  const { data } = await instantClientDB.queryOnce({
    daysCompleted: {
      $: {
        where: {
          date: date,
        },
      },
      routine: {
        $: {
          where: {
            id: routineId,
          },
        },
      },
    },
  });

  const existingDayCompleted = data?.daysCompleted?.find((dc) => dc.routine?.id === routineId);

  const now = new Date().toISOString();

  if (existingDayCompleted) {
    // Update existing record
    await instantClientDB.transact([
      instantClientDB.tx.daysCompleted[existingDayCompleted.id].update({
        stepsCompleted,
        completed: completed ?? stepsCompleted.length > 0,
        updatedAt: now,
      }),
    ]);
    return existingDayCompleted.id;
  }

  // Create new record
  const dayCompletedId = id();
  await instantClientDB.transact([
    instantClientDB.tx.daysCompleted[dayCompletedId].update({
      date,
      stepsCompleted,
      completed: completed ?? stepsCompleted.length > 0,
      createdAt: now,
      updatedAt: now,
    }),
    instantClientDB.tx.routines[routineId].link({ daysCompleted: [dayCompletedId] }),
    instantClientDB.tx.users[userId].link({ daysCompleted: [dayCompletedId] }),
  ]);
  return dayCompletedId;
}
